import React, { useState } from "react";

import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Switch } from "@/shared/components/ui/switch";
import { AppLogo } from "@/shared/components/AppLogo";
import { Chip } from "@/shared/components/ui-kit";
import { DangerButton, confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import { useDeleteConnection, usePatchConnection } from "@/shared/api/apps";

import type { AppConnection, ConnectionStatus } from "@/shared/types";

const STATUS_TONE: Record<ConnectionStatus, React.ComponentProps<typeof Chip>["tone"]> = {
  connecting: "warn",
  connected: "success",
  failed: "danger",
};

/* One authorized account. `account_label` is the account's own identity at the
   provider — a Gmail address, a Slack workspace — so it is the row's name, and
   the app name is only the fallback for a provider that grants none.

   `mine` and `visibility` answer two different questions: whose credential it
   is, and who may use it. Both are on the row because "why am I seeing this?"
   is the first thing asked of a list that mixes them.

   Both writes are admin-gated the way the service is (`apps.share`, CONTRACT
   §6): acting on someone else's connection and sharing one with the org are
   the same kind of act, so a member is shown neither rather than shown a
   button that always answers 403. */
export function ConnectionRow({ connection, canShare }: { connection: AppConnection; canShare: boolean }) {
  const [confirming, setConfirming] = useState(false);
  const share = usePatchConnection();
  const revoke = useDeleteConnection();
  const name = connection.account_label ?? connection.app.name;
  const mayManage = connection.mine || canShare;

  return (
    /* The actions move to their own line when the row is too narrow for them beside the name */
    <div className="border-t hairline first:border-t-0 flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
      <AppLogo logo={connection.app.logo} name={connection.app.name} size={22} iconSize={13} rounded={6} />
      <div className="min-w-0 flex-1 basis-[160px]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em] truncate">
            {name}
          </span>
          <Chip tone={STATUS_TONE[connection.status]}>{connection.status}</Chip>
        </div>
        <div className="text-[12px] text-[var(--text-tertiary)] mt-[1px]">
          {connection.visibility === "org" ? "Shared with the workspace" : "Private to you"}
          {connection.mine ? "" : " · authorized by a teammate"}
        </div>
      </div>

      <div className="ml-auto flex items-center gap-3 shrink-0">
        {canShare && connection.status === "connected" && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="whitespace-nowrap text-[12.5px] text-[var(--text-secondary)]">Share with workspace</span>
            <Switch
              aria-label="Share with workspace"
              checked={connection.visibility === "org"}
              disabled={share.isPending}
              onCheckedChange={(on) =>
                share.mutate({ id: connection.id, body: { visibility: on ? "org" : "private" } })
              }
            />
          </div>
        )}

        {mayManage && <DangerButton onClick={() => setConfirming(true)}>Disconnect</DangerButton>}
      </div>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Disconnect {name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              The credential is revoked at {connection.app.name} first. Every agent linked to this
              account loses its tools and its event subscriptions with it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full text-[13px]">Cancel</AlertDialogCancel>
            <button
              className={confirmDangerCls}
              onClick={() => {
                setConfirming(false);
                revoke.mutate(connection.id);
              }}
            >
              Disconnect
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
